/**
 * Operation Lock Service for Multi-Account Management
 * Prevents concurrent account operations that could cause race conditions
 */

export class OperationLockService {
  private static locks = new Map<string, boolean>();
  private static readonly GLOBAL_LOCK = 'global_account_operation';

  /**
   * Acquire a lock for the specified operation
   */
  static async acquireLock(lockName: string = this.GLOBAL_LOCK, timeoutMs: number = 10000): Promise<boolean> {
    const startTime = Date.now();
    
    while (this.locks.get(lockName) === true) {
      if (Date.now() - startTime > timeoutMs) {
        console.warn(`OperationLockService: Timeout waiting for lock ${lockName}`);
        return false;
      }
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    this.locks.set(lockName, true);
    console.log(`OperationLockService: Acquired lock ${lockName}`);
    return true;
  }

  /**
   * Release a lock for the specified operation
   */
  static releaseLock(lockName: string = this.GLOBAL_LOCK): void {
    this.locks.set(lockName, false);
    console.log(`OperationLockService: Released lock ${lockName}`);
  }

  /**
   * Check if a lock is currently held
   */
  static isLocked(lockName: string = this.GLOBAL_LOCK): boolean {
    return this.locks.get(lockName) === true;
  }

  /**
   * Force release all locks (emergency cleanup)
   */
  static forceReleaseAll(): void {
    console.warn('OperationLockService: Force releasing all locks');
    this.locks.clear();
  }

  /**
   * Wait for a specific lock to be released
   */
  static async waitForLockRelease(lockName: string = this.GLOBAL_LOCK, timeoutMs: number = 5000): Promise<boolean> {
    const startTime = Date.now();
    
    while (this.locks.get(lockName) === true) {
      if (Date.now() - startTime > timeoutMs) {
        console.warn(`OperationLockService: Timeout waiting for lock release ${lockName}`);
        return false;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return true;
  }
}